import {
  COLORSET_ID,
  PLATFORM_VERTICAL_KEYS,
  THEME_ID,
  type ColorSetId,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { FAMILY_ID } from '../matrix/familyRecipes';
import { CORNER_PRESETS, TYPOGRAPHY_PRESETS } from './themePresets';
import { THEME_BLOCK_PRESENTATIONS } from './themeBlockPresentations';
import {
  CORNER_ENUM_KEYS,
  SPACING_ENUM_KEYS,
  TYPOGRAPHY_ENUM_KEYS,
  type ColorSetTokenMap,
  type ThemeColorSetDefinition,
  type ThemeRegistryEntry,
} from './types';

type ThemeRegistryDefinition = Omit<ThemeRegistryEntry, 'themeId'>;

function themeEntry(
  themeId: ThemeId,
  definition: Omit<ThemeRegistryDefinition, 'blockPresentations'>,
): ThemeRegistryEntry {
  return {
    themeId,
    ...definition,
    blockPresentations: THEME_BLOCK_PRESENTATIONS[themeId],
  };
}

function palette(
  id: ColorSetId,
  name: string,
  tokens: ColorSetTokenMap,
): ThemeColorSetDefinition {
  return { id, name, tokens };
}

/** Authoritative theme + color-set registry (OKLCH only). */
export const THEME_REGISTRY: Record<ThemeId, ThemeRegistryEntry> = {
  [THEME_ID.michelinDark]: themeEntry(THEME_ID.michelinDark, {
    name: 'Michelin Dark',
    familyId: FAMILY_ID.fineDining,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.champagneNight,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.editorialSerif,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.editorialSerif],
      spacing: SPACING_ENUM_KEYS.relaxed,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — soft anthracite salon; champagne jewel + blush garnish (not gold-on-gold). */
      palette(COLORSET_ID.champagneNight, 'Champagne Night', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.78 0.12 85)',
        brandAccent: 'oklch(0.85 0.1 90)',
      }),
      /** Velvet smoke — warm peat soot + ember jewel (not Champagne anthracite twin). */
      palette(COLORSET_ID.velvetSmoke, 'Velvet Smoke', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.62 0.14 25)',
        brandAccent: 'oklch(0.72 0.1 75)',
      }),
      /** Platinum night — cool blue-black anthracite (distinct ground from Velvet). */
      palette(COLORSET_ID.platinumDawn, 'Platinum Dawn', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.82 0.04 240)',
        brandAccent: 'oklch(0.88 0.06 200)',
      }),
      /** Day service — warm ivory, champagne jewel + cool platinum garnish. */
      palette(COLORSET_ID.ivoryChampagne, 'Ivory Champagne', {
        bgBase: 'oklch(0.97 0.012 90)',
        bgSurface: 'oklch(0.995 0.004 90)',
        textMain: 'oklch(0.22 0.02 50)',
        textMuted: 'oklch(0.48 0.02 55)',
        brandPrimary: 'oklch(0.62 0.1 85)',
        brandAccent: 'oklch(0.72 0.08 75)',
      }),
      /** Day service — mist porcelain + soft platinum ink; champagne warm garnish (pre-refresh soft). */
      palette(COLORSET_ID.porcelainSilver, 'Porcelain Silver', {
        bgBase: 'oklch(0.97 0.008 250)',
        bgSurface: 'oklch(0.995 0.003 250)',
        textMain: 'oklch(0.2 0.02 260)',
        textMuted: 'oklch(0.46 0.015 250)',
        brandPrimary: 'oklch(0.42 0.03 250)',
        brandAccent: 'oklch(0.68 0.08 85)',
      }),
    ],
  }),
  /**
   * Fine dining daylight — limestone paper, glasshouse green, not Michelin anthracite.
   * Pill chrome + Newsreader/Figtree; Conservatory Chalk is the house light.
   */
  [THEME_ID.limestoneOrangery]: themeEntry(THEME_ID.limestoneOrangery, {
    name: 'Limestone Orangery',
    familyId: FAMILY_ID.fineDining,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.conservatoryChalk,
    corner_enum: CORNER_ENUM_KEYS.pill,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.limestoneOrangery,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.limestoneOrangery],
      spacing: SPACING_ENUM_KEYS.relaxed,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.pill],
    color_sets: [
      /** Poster — pre-refresh mist chalk glasshouse + leaf jewel. */
      palette(COLORSET_ID.conservatoryChalk, 'Conservatory Chalk', {
        bgBase: 'oklch(0.97 0.012 108)',
        bgSurface: 'oklch(0.995 0.006 108)',
        textMain: 'oklch(0.22 0.03 145)',
        textMuted: 'oklch(0.48 0.025 140)',
        brandPrimary: 'oklch(0.42 0.08 145)',
        brandAccent: 'oklch(0.68 0.1 95)',
      }),
      /** Pre-refresh espalier paper; citrus jewel nudged off hummus twin. */
      palette(COLORSET_ID.espalierCitrus, 'Espalier Citrus', {
        bgBase: 'oklch(0.97 0.02 95)',
        bgSurface: 'oklch(0.995 0.01 95)',
        textMain: 'oklch(0.24 0.04 85)',
        textMuted: 'oklch(0.48 0.03 90)',
        brandPrimary: 'oklch(0.56 0.12 78)',
        brandAccent: 'oklch(0.58 0.12 158)',
      }),
      /** Pre-refresh dusk pane — cool glass after hours. */
      palette(COLORSET_ID.duskPane, 'Dusk Pane', {
        bgBase: 'oklch(0.18 0.03 240)',
        bgSurface: 'oklch(0.24 0.035 240)',
        textMain: 'oklch(0.9 0.02 108)',
        textMuted: 'oklch(0.72 0.025 200)',
        brandPrimary: 'oklch(0.72 0.08 145)',
        brandAccent: 'oklch(0.78 0.1 95)',
      }),
      /** Soft apricot trellis — warm glasshouse peach (not Herb Mist green twin). */
      palette(COLORSET_ID.apricotTrellis, 'Apricot Trellis', {
        bgBase: 'oklch(0.97 0.02 55)',
        bgSurface: 'oklch(0.995 0.008 55)',
        textMain: 'oklch(0.22 0.04 45)',
        textMuted: 'oklch(0.48 0.03 50)',
        brandPrimary: 'oklch(0.55 0.12 48)',
        brandAccent: 'oklch(0.5 0.06 250)',
      }),
      /**
       * Stance dark #2 — was weak day frost pre-refresh (4L/1D).
       * Soft cool glasshouse night so chalk/espalier/apricot stay mist day paper.
       */
      palette(COLORSET_ID.frostPane, 'Frost Pane', {
        bgBase: 'oklch(0.15 0.02 255)',
        bgSurface: 'oklch(0.21 0.025 255)',
        textMain: 'oklch(0.92 0.012 250)',
        textMuted: 'oklch(0.72 0.018 248)',
        brandPrimary: 'oklch(0.74 0.06 258)',
        brandAccent: 'oklch(0.7 0.09 145)',
      }),
    ],
  }),
  /**
   * Fine dining estate — peat linen, hearth rust, not bistro cream and not Michelin night.
   * Sharp corners + Spectral/Schibsted; Peat Linen is the house paper.
   */
  [THEME_ID.moorlandHearth]: themeEntry(THEME_ID.moorlandHearth, {
    name: 'Moorland Hearth',
    familyId: FAMILY_ID.fineDining,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.peatLinen,
    corner_enum: CORNER_ENUM_KEYS.sharp,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.moorlandHearth,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.moorlandHearth],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.sharp],
    color_sets: [
      /** Poster — soft peat linen; hearth rust jewel + beeswax whisper. */
      palette(COLORSET_ID.peatLinen, 'Peat Linen', {
        bgBase: 'oklch(0.95 0.018 72)',
        bgSurface: 'oklch(0.985 0.01 72)',
        textMain: 'oklch(0.22 0.04 45)',
        textMuted: 'oklch(0.48 0.03 50)',
        brandPrimary: 'oklch(0.48 0.1 40)',
        brandAccent: 'oklch(0.62 0.12 55)',
      }),
      /** Oak smoke — evening peat walls; beeswax jewel + canal cool garnish. */
      palette(COLORSET_ID.oakSmoke, 'Oak Smoke', {
        bgBase: 'oklch(0.16 0.03 50)',
        bgSurface: 'oklch(0.22 0.035 48)',
        textMain: 'oklch(0.9 0.02 80)',
        textMuted: 'oklch(0.72 0.025 60)',
        brandPrimary: 'oklch(0.68 0.1 55)',
        brandAccent: 'oklch(0.62 0.12 40)',
      }),
      /** Grouse — deeper copper rust + cool canal garnish (not Peat Linen twin). */
      palette(COLORSET_ID.grouseRust, 'Grouse Rust', {
        bgBase: 'oklch(0.94 0.025 50)',
        bgSurface: 'oklch(0.98 0.012 55)',
        textMain: 'oklch(0.2 0.05 40)',
        textMuted: 'oklch(0.46 0.04 45)',
        brandPrimary: 'oklch(0.52 0.14 40)',
        brandAccent: 'oklch(0.58 0.08 80)',
      }),
      /** Canal after dark — cool mist night (second dark beside Oak Smoke). */
      palette(COLORSET_ID.canalMist, 'Canal Mist', {
        bgBase: 'oklch(0.15 0.022 220)',
        bgSurface: 'oklch(0.21 0.026 220)',
        textMain: 'oklch(0.92 0.015 230)',
        textMuted: 'oklch(0.72 0.02 220)',
        brandPrimary: 'oklch(0.7 0.06 230)',
        brandAccent: 'oklch(0.62 0.1 40)',
      }),
      palette(COLORSET_ID.beeswaxGold, 'Beeswax Gold', {
        bgBase: 'oklch(0.95 0.03 85)',
        bgSurface: 'oklch(0.985 0.015 85)',
        textMain: 'oklch(0.22 0.04 70)',
        textMuted: 'oklch(0.48 0.03 75)',
        brandPrimary: 'oklch(0.58 0.11 80)',
        brandAccent: 'oklch(0.5 0.1 40)',
      }),
    ],
  }),
  [THEME_ID.bistroBrasserie]: themeEntry(THEME_ID.bistroBrasserie, {
    name: 'Bistro & Brasserie',
    familyId: FAMILY_ID.bistro,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.classicBrass,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.bistroWarm,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.bistroWarm],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — soft brass on misty zinc paper; cork garnish (not Michelin gold twin). */
      palette(COLORSET_ID.classicBrass, 'Classic Brass', {
        bgBase: 'oklch(0.96 0.01 85)',
        bgSurface: 'oklch(1 0 0)',
        textMain: 'oklch(0.2 0.02 45)',
        textMuted: 'oklch(0.48 0.02 45)',
        brandPrimary: 'oklch(0.52 0.09 58)',
        brandAccent: 'oklch(0.68 0.1 85)',
      }),
      /** Soft linen morning — sage breakfast accent. */
      palette(COLORSET_ID.linenMorning, 'Linen Morning', {
        bgBase: 'oklch(0.98 0.01 95)',
        bgSurface: 'oklch(1 0 0)',
        textMain: 'oklch(0.22 0.02 55)',
        textMuted: 'oklch(0.5 0.02 55)',
        brandPrimary: 'oklch(0.48 0.07 125)',
        brandAccent: 'oklch(0.62 0.09 160)',
      }),
      /** Soft copper plate — jewel in primary, mist cream (not clay-broth twin). */
      palette(COLORSET_ID.copperGlow, 'Copper Glow', {
        bgBase: 'oklch(0.94 0.03 45)',
        bgSurface: 'oklch(0.98 0.02 55)',
        textMain: 'oklch(0.18 0.04 35)',
        textMuted: 'oklch(0.45 0.03 40)',
        brandPrimary: 'oklch(0.55 0.13 32)',
        brandAccent: 'oklch(0.65 0.1 165)',
      }),
      /** Soft zinc dusk — pewter after close, brass spark. */
      palette(COLORSET_ID.zincApron, 'Zinc Apron', {
        bgBase: 'oklch(0.15 0.02 255)',
        bgSurface: 'oklch(0.21 0.025 255)',
        textMain: 'oklch(0.92 0.012 250)',
        textMuted: 'oklch(0.72 0.018 250)',
        brandPrimary: 'oklch(0.72 0.05 260)',
        brandAccent: 'oklch(0.7 0.1 75)',
      }),
      /** Soft olive banquet — evening wall, warm brass thread. */
      palette(COLORSET_ID.oliveBanquette, 'Olive Banquette', {
        bgBase: 'oklch(0.16 0.035 125)',
        bgSurface: 'oklch(0.22 0.04 125)',
        textMain: 'oklch(0.9 0.02 95)',
        textMuted: 'oklch(0.72 0.025 120)',
        brandPrimary: 'oklch(0.62 0.1 125)',
        brandAccent: 'oklch(0.72 0.12 70)',
      }),
    ],
  }),
  /**
   * Sushi counter — nigiri on nori night by default (not Michelin anthracite,
   * not lantern wood). Dark majority (3) with wasabi + shari as day paper.
   */
  [THEME_ID.hinokiCounter]: themeEntry(THEME_ID.hinokiCounter, {
    name: 'Hinoki Counter',
    familyId: FAMILY_ID.asian,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.midnightNori,
    corner_enum: CORNER_ENUM_KEYS.subtle,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.counterPrecision,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.counterPrecision],
      /** Counter film — room between Chef’s Cut / Omakase / sitting (not compact stack). */
      spacing: SPACING_ENUM_KEYS.relaxed,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.subtle],
    color_sets: [
      /** Poster — soft seaweed night so nigiri pops (pre-refresh misty nori). */
      palette(COLORSET_ID.midnightNori, 'Midnight Nori', {
        bgBase: 'oklch(0.12 0.028 165)',
        bgSurface: 'oklch(0.18 0.032 165)',
        textMain: 'oklch(0.9 0.015 150)',
        textMuted: 'oklch(0.72 0.02 155)',
        brandPrimary: 'oklch(0.72 0.1 165)',
        brandAccent: 'oklch(0.78 0.1 25)',
      }),
      /** Slate night — blue stone, wasabi spark. */
      palette(COLORSET_ID.slateCounter, 'Slate Counter', {
        bgBase: 'oklch(0.13 0.035 235)',
        bgSurface: 'oklch(0.19 0.04 235)',
        textMain: 'oklch(0.92 0.015 240)',
        textMuted: 'oklch(0.72 0.02 235)',
        brandPrimary: 'oklch(0.68 0.09 232)',
        brandAccent: 'oklch(0.65 0.12 145)',
      }),
      /** Wasabi noon — sharp green jewel, ginger garnish (not Limestone chalk gold). */
      palette(COLORSET_ID.wasabiPaper, 'Wasabi Paper', {
        bgBase: 'oklch(0.98 0.02 150)',
        bgSurface: 'oklch(0.995 0.008 150)',
        textMain: 'oklch(0.2 0.04 155)',
        textMuted: 'oklch(0.46 0.035 150)',
        brandPrimary: 'oklch(0.52 0.16 152)',
        brandAccent: 'oklch(0.62 0.12 45)',
      }),
      /** Shari ivory — warm rice paper, soy ink, toasted gari spark (not Ivory Champagne twin). */
      palette(COLORSET_ID.shariIvory, 'Shari Ivory', {
        bgBase: 'oklch(0.98 0.02 95)',
        bgSurface: 'oklch(0.995 0.008 92)',
        textMain: 'oklch(0.22 0.04 50)',
        textMuted: 'oklch(0.48 0.03 55)',
        brandPrimary: 'oklch(0.38 0.055 55)',
        brandAccent: 'oklch(0.58 0.15 38)',
      }),
      /** Cedar night — warm plank after service. */
      palette(COLORSET_ID.cedarPlank, 'Cedar Plank', {
        bgBase: 'oklch(0.14 0.035 55)',
        bgSurface: 'oklch(0.2 0.04 55)',
        textMain: 'oklch(0.92 0.02 55)',
        textMuted: 'oklch(0.72 0.025 50)',
        brandPrimary: 'oklch(0.68 0.1 50)',
        brandAccent: 'oklch(0.72 0.12 25)',
      }),
    ],
  }),
  /** Vietnamese neighborhood — herb steam / broth warmth; not bistro cream, not sushi slate. */
  [THEME_ID.lotusSteam]: themeEntry(THEME_ID.lotusSteam, {
    name: 'Lotus Steam',
    familyId: FAMILY_ID.asian,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.herbMist,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.herbSteam,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.herbSteam],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — herb mist with chili-oil spark. */
      palette(COLORSET_ID.herbMist, 'Herb Mist', {
        bgBase: 'oklch(0.97 0.015 145)',
        bgSurface: 'oklch(0.995 0.006 145)',
        textMain: 'oklch(0.22 0.04 150)',
        textMuted: 'oklch(0.46 0.03 145)',
        brandPrimary: 'oklch(0.42 0.1 145)',
        brandAccent: 'oklch(0.62 0.14 55)',
      }),
      palette(COLORSET_ID.clayBroth, 'Clay Broth', {
        bgBase: 'oklch(0.95 0.035 62)',
        bgSurface: 'oklch(0.99 0.016 65)',
        textMain: 'oklch(0.2 0.045 48)',
        textMuted: 'oklch(0.45 0.035 52)',
        brandPrimary: 'oklch(0.48 0.14 48)',
        brandAccent: 'oklch(0.55 0.12 165)',
      }),
      palette(COLORSET_ID.midnightLotus, 'Midnight Lotus', {
        bgBase: 'oklch(0.15 0.045 175)',
        bgSurface: 'oklch(0.21 0.05 175)',
        textMain: 'oklch(0.92 0.025 165)',
        textMuted: 'oklch(0.72 0.03 170)',
        brandPrimary: 'oklch(0.7 0.14 178)',
        brandAccent: 'oklch(0.7 0.14 55)',
      }),
      /** Banana leaf steam — soft wrap paper; turmeric jewel (not Herb Mist chili twin). */
      palette(COLORSET_ID.bananaSteam, 'Banana Steam', {
        bgBase: 'oklch(0.97 0.028 112)',
        bgSurface: 'oklch(0.995 0.012 110)',
        textMain: 'oklch(0.22 0.045 125)',
        textMuted: 'oklch(0.46 0.03 118)',
        brandPrimary: 'oklch(0.42 0.12 125)',
        brandAccent: 'oklch(0.68 0.14 85)',
      }),
      /** Saffron night — spice steam after dark (second dark beside Midnight Lotus). */
      palette(COLORSET_ID.saffronBowl, 'Saffron Bowl', {
        bgBase: 'oklch(0.15 0.04 70)',
        bgSurface: 'oklch(0.21 0.045 75)',
        textMain: 'oklch(0.92 0.025 85)',
        textMuted: 'oklch(0.72 0.03 70)',
        brandPrimary: 'oklch(0.72 0.14 75)',
        brandAccent: 'oklch(0.62 0.12 145)',
      }),
    ],
  }),
  /**
   * Vietnamese neighborhood #2 — tiled shopfront / awning market.
   * Sharp geometry, compact rhythm, full-bleed facade — not a Lotus Steam retint.
   */
  [THEME_ID.tileAwning]: themeEntry(THEME_ID.tileAwning, {
    name: 'Tile Awning',
    familyId: FAMILY_ID.asian,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.ceramicDawn,
    corner_enum: CORNER_ENUM_KEYS.sharp,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.tileAwning,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.tileAwning],
      spacing: SPACING_ENUM_KEYS.compact,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.sharp],
    color_sets: [
      /** Poster — soft ceramic shopfront (pre-refresh misty indigo). */
      palette(COLORSET_ID.ceramicDawn, 'Ceramic Dawn', {
        bgBase: 'oklch(0.96 0.012 230)',
        bgSurface: 'oklch(0.99 0.006 230)',
        textMain: 'oklch(0.2 0.03 240)',
        textMuted: 'oklch(0.46 0.025 235)',
        brandPrimary: 'oklch(0.42 0.06 235)',
        brandAccent: 'oklch(0.58 0.16 35)',
      }),
      /** Soft cayenne awning + turmeric spark (misty paper; jewel in primary). */
      palette(COLORSET_ID.cayenneStripe, 'Cayenne Stripe', {
        bgBase: 'oklch(0.97 0.016 45)',
        bgSurface: 'oklch(0.995 0.007 45)',
        textMain: 'oklch(0.2 0.04 28)',
        textMuted: 'oklch(0.46 0.025 38)',
        brandPrimary: 'oklch(0.46 0.14 8)',
        brandAccent: 'oklch(0.68 0.11 115)',
      }),
      /** Soft ink dusk — chili jewel, turmeric garnish (pre-refresh mist). */
      palette(COLORSET_ID.inkShop, 'Ink Shop', {
        bgBase: 'oklch(0.15 0.02 240)',
        bgSurface: 'oklch(0.21 0.025 240)',
        textMain: 'oklch(0.92 0.015 230)',
        textMuted: 'oklch(0.72 0.02 235)',
        brandPrimary: 'oklch(0.72 0.12 35)',
        brandAccent: 'oklch(0.78 0.1 85)',
      }),
      /** Grout lime — chartreuse tile joint (not Lotus Banana Steam ~125°). */
      palette(COLORSET_ID.groutLime, 'Grout Lime', {
        bgBase: 'oklch(0.97 0.03 110)',
        bgSurface: 'oklch(0.995 0.012 108)',
        textMain: 'oklch(0.2 0.04 118)',
        textMuted: 'oklch(0.46 0.03 112)',
        brandPrimary: 'oklch(0.5 0.14 110)',
        brandAccent: 'oklch(0.55 0.12 35)',
      }),
      /** Soft monsoon dusk — misty wet-tile evening (low-C; not night punch). */
      palette(COLORSET_ID.monsoonBlue, 'Monsoon Blue', {
        bgBase: 'oklch(0.17 0.022 248)',
        bgSurface: 'oklch(0.23 0.026 248)',
        textMain: 'oklch(0.92 0.015 240)',
        textMuted: 'oklch(0.72 0.02 245)',
        brandPrimary: 'oklch(0.72 0.1 248)',
        brandAccent: 'oklch(0.7 0.12 38)',
      }),
    ],
  }),
  /**
   * Pan-Asian courtyard — Ember Lantern is poster/default; celadon + ink wash noon paper.
   * Jewel spark lives in primary/accent, not pumped ground chroma.
   */
  [THEME_ID.lanternCourt]: themeEntry(THEME_ID.lanternCourt, {
    name: 'Lantern Court',
    familyId: FAMILY_ID.asian,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.emberLantern,
    corner_enum: CORNER_ENUM_KEYS.subtle,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.lanternCourt,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.lanternCourt],
      spacing: SPACING_ENUM_KEYS.relaxed,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.subtle],
    color_sets: [
      /** Poster — soft ember dusk wood + lantern gold (pre-refresh mist). */
      palette(COLORSET_ID.emberLantern, 'Ember Lantern', {
        bgBase: 'oklch(0.16 0.042 48)',
        bgSurface: 'oklch(0.22 0.045 46)',
        textMain: 'oklch(0.88 0.03 72)',
        textMuted: 'oklch(0.72 0.035 58)',
        brandPrimary: 'oklch(0.74 0.13 72)',
        brandAccent: 'oklch(0.58 0.14 28)',
      }),
      /** Soft lacquer cabinet — crimson jewel, celadon stamp (not Ember gold↔red swap). */
      palette(COLORSET_ID.lacquerPaper, 'Lacquer Dusk', {
        bgBase: 'oklch(0.14 0.03 22)',
        bgSurface: 'oklch(0.2 0.034 24)',
        textMain: 'oklch(0.9 0.02 70)',
        textMuted: 'oklch(0.72 0.025 35)',
        brandPrimary: 'oklch(0.62 0.13 18)',
        brandAccent: 'oklch(0.72 0.08 175)',
      }),
      /** Soft plum dusk silk — plum jewel + chili garnish. */
      palette(COLORSET_ID.plumSilk, 'Plum Silk', {
        bgBase: 'oklch(0.16 0.028 325)',
        bgSurface: 'oklch(0.22 0.032 325)',
        textMain: 'oklch(0.9 0.02 70)',
        textMuted: 'oklch(0.72 0.025 330)',
        brandPrimary: 'oklch(0.62 0.09 320)',
        brandAccent: 'oklch(0.7 0.11 42)',
      }),
      /** Soft celadon noon — pale glaze lunch paper. */
      palette(COLORSET_ID.celadonCourt, 'Celadon Court', {
        bgBase: 'oklch(0.96 0.025 165)',
        bgSurface: 'oklch(0.99 0.01 165)',
        textMain: 'oklch(0.22 0.04 170)',
        textMuted: 'oklch(0.46 0.03 165)',
        brandPrimary: 'oklch(0.45 0.08 170)',
        brandAccent: 'oklch(0.62 0.1 40)',
      }),
      /** Noon ink wash — cool paper beside celadon. */
      palette(COLORSET_ID.inkWash, 'Ink Wash', {
        bgBase: 'oklch(0.96 0.01 250)',
        bgSurface: 'oklch(0.99 0.005 250)',
        textMain: 'oklch(0.2 0.02 260)',
        textMuted: 'oklch(0.46 0.02 250)',
        brandPrimary: 'oklch(0.35 0.04 260)',
        brandAccent: 'oklch(0.55 0.12 28)',
      }),
    ],
  }),
  [THEME_ID.brutalistGrind]: themeEntry(THEME_ID.brutalistGrind, {
    name: 'Brutalist Grind',
    familyId: FAMILY_ID.cafe,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.cafe],
    default_colorset_id: COLORSET_ID.espressoCore,
    corner_enum: CORNER_ENUM_KEYS.sharp,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.brutalGrotesk,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.brutalGrotesk],
      spacing: SPACING_ENUM_KEYS.compact,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.sharp],
    color_sets: [
      palette(COLORSET_ID.espressoCore, 'Espresso Core', {
        bgBase: 'oklch(0.96 0 0)',
        bgSurface: 'oklch(1 0 0)',
        textMain: 'oklch(0.1 0 0)',
        textMuted: 'oklch(0.4 0 0)',
        brandPrimary: 'oklch(0.2 0 0)',
        brandAccent: 'oklch(0.65 0.22 45)',
      }),
      palette(COLORSET_ID.paperWhite, 'Paper White', {
        bgBase: 'oklch(0.99 0 0)',
        bgSurface: 'oklch(1 0 0)',
        textMain: 'oklch(0.15 0 0)',
        textMuted: 'oklch(0.45 0 0)',
        brandPrimary: 'oklch(0.25 0 0)',
        brandAccent: 'oklch(0.55 0.18 45)',
      }),
      /** Poster — construction signal on bleach paper. */
      palette(COLORSET_ID.signalOrange, 'Signal Orange', {
        bgBase: 'oklch(0.97 0 0)',
        bgSurface: 'oklch(1 0 0)',
        textMain: 'oklch(0.12 0 0)',
        textMuted: 'oklch(0.42 0 0)',
        brandPrimary: 'oklch(0.64 0.22 52)',
        brandAccent: 'oklch(0.4 0.04 250)',
      }),
      /** Concrete night — dusty rose on soot (second dark beside Matcha Grid). */
      palette(COLORSET_ID.concreteRose, 'Concrete Rose', {
        bgBase: 'oklch(0.14 0.02 20)',
        bgSurface: 'oklch(0.2 0.025 20)',
        textMain: 'oklch(0.92 0.015 25)',
        textMuted: 'oklch(0.72 0.02 20)',
        brandPrimary: 'oklch(0.7 0.08 18)',
        brandAccent: 'oklch(0.68 0.12 45)',
      }),
      /** After-hours grind — bitter matcha grid, chalk line accent (not Lotus twin). */
      palette(COLORSET_ID.matchaGrid, 'Matcha Grid', {
        bgBase: 'oklch(0.14 0.025 140)',
        bgSurface: 'oklch(0.2 0.03 140)',
        textMain: 'oklch(0.9 0.02 140)',
        textMuted: 'oklch(0.7 0.025 140)',
        brandPrimary: 'oklch(0.68 0.14 140)',
        brandAccent: 'oklch(0.72 0.12 45)',
      }),
    ],
  }),
  [THEME_ID.earthClay]: themeEntry(THEME_ID.earthClay, {
    name: 'Earth Clay',
    familyId: FAMILY_ID.cafe,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.cafe],
    default_colorset_id: COLORSET_ID.terracottaDusk,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.earthOrganic,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.earthOrganic],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — terracotta slip with indigo thread. */
      palette(COLORSET_ID.terracottaDusk, 'Terracotta Dusk', {
        bgBase: 'oklch(0.94 0.02 65)',
        bgSurface: 'oklch(0.98 0.01 65)',
        textMain: 'oklch(0.22 0.03 55)',
        textMuted: 'oklch(0.5 0.03 55)',
        brandPrimary: 'oklch(0.46 0.09 38)',
        brandAccent: 'oklch(0.6 0.1 195)',
      }),
      palette(COLORSET_ID.sageMist, 'Sage Mist', {
        bgBase: 'oklch(0.96 0.02 140)',
        bgSurface: 'oklch(0.99 0.01 140)',
        textMain: 'oklch(0.2 0.03 140)',
        textMuted: 'oklch(0.48 0.03 140)',
        brandPrimary: 'oklch(0.45 0.08 140)',
        brandAccent: 'oklch(0.62 0.1 120)',
      }),
      /** Closed kiln — terracotta fire on soot (not lantern gold). */
      palette(COLORSET_ID.kilnEmber, 'Kiln Ember', {
        bgBase: 'oklch(0.15 0.045 35)',
        bgSurface: 'oklch(0.21 0.05 38)',
        textMain: 'oklch(0.9 0.03 65)',
        textMuted: 'oklch(0.7 0.035 45)',
        brandPrimary: 'oklch(0.65 0.14 40)',
        brandAccent: 'oklch(0.72 0.12 70)',
      }),
      /** Indigo night — dyed cloth after closing (second dark beside Kiln Ember). */
      palette(COLORSET_ID.indigoLoom, 'Indigo Loom', {
        bgBase: 'oklch(0.14 0.03 270)',
        bgSurface: 'oklch(0.2 0.035 270)',
        textMain: 'oklch(0.92 0.015 265)',
        textMuted: 'oklch(0.72 0.02 265)',
        brandPrimary: 'oklch(0.68 0.1 270)',
        brandAccent: 'oklch(0.7 0.1 55)',
      }),
      /** Butter slip — kiln cream, cobalt oxide garnish (not beeswax twin). */
      palette(COLORSET_ID.butterSlip, 'Butter Slip', {
        bgBase: 'oklch(0.97 0.04 95)',
        bgSurface: 'oklch(0.995 0.02 95)',
        textMain: 'oklch(0.22 0.04 70)',
        textMuted: 'oklch(0.48 0.03 80)',
        brandPrimary: 'oklch(0.52 0.1 98)',
        brandAccent: 'oklch(0.48 0.1 250)',
      }),
    ],
  }),
  [THEME_ID.neonNoir]: themeEntry(THEME_ID.neonNoir, {
    name: 'Neon Noir',
    familyId: FAMILY_ID.bar,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.bar],
    default_colorset_id: COLORSET_ID.electricViolet,
    corner_enum: CORNER_ENUM_KEYS.subtle,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.neonDisplay,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.neonDisplay],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.subtle],
    color_sets: [
      /** Poster — electric violet tube on noir. */
      palette(COLORSET_ID.electricViolet, 'Electric Violet', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.62 0.31 320)',
        brandAccent: 'oklch(0.74 0.28 175)',
      }),
      palette(COLORSET_ID.cyanPulse, 'Cyan Pulse', {
        bgBase: 'oklch(0.97 0.02 200)',
        bgSurface: 'oklch(0.995 0.008 200)',
        textMain: 'oklch(0.2 0.04 220)',
        textMuted: 'oklch(0.46 0.03 210)',
        brandPrimary: 'oklch(0.5 0.14 200)',
        brandAccent: 'oklch(0.55 0.16 320)',
      }),
      /** Day glass — matinee paper with violet ink (second light beside Acid Chartreuse). */
      palette(COLORSET_ID.midnightGlass, 'Midnight Glass', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.55 0.2 300)',
        brandAccent: 'oklch(0.7 0.15 85)',
      }),
      /** Day flyer — acid paper, violet ink (matinee / print). */
      palette(COLORSET_ID.acidChartreuse, 'Acid Chartreuse', {
        bgBase: 'oklch(0.96 0.04 125)',
        bgSurface: 'oklch(0.99 0.02 125)',
        textMain: 'oklch(0.18 0.05 285)',
        textMuted: 'oklch(0.42 0.04 280)',
        brandPrimary: 'oklch(0.52 0.2 125)',
        brandAccent: 'oklch(0.5 0.22 320)',
      }),
      /** Blood neon — orange tube on soot, cyan pulse garnish (not street sunset twin). */
      palette(COLORSET_ID.bloodOrange, 'Blood Orange', {
        bgBase: 'oklch(0.1 0.03 32)',
        bgSurface: 'oklch(0.16 0.035 32)',
        textMain: 'oklch(0.92 0.03 50)',
        textMuted: 'oklch(0.72 0.04 45)',
        brandPrimary: 'oklch(0.68 0.16 42)',
        brandAccent: 'oklch(0.72 0.1 200)',
      }),
    ],
  }),
  [THEME_ID.velvetVintage]: themeEntry(THEME_ID.velvetVintage, {
    name: 'Velvet Vintage',
    familyId: FAMILY_ID.bar,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.bar],
    default_colorset_id: COLORSET_ID.burgundyLounge,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.vintageSerif,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.vintageSerif],
      spacing: SPACING_ENUM_KEYS.relaxed,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — burgundy lounge velvet. */
      palette(COLORSET_ID.burgundyLounge, 'Burgundy Lounge', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.52 0.14 25)',
        brandAccent: 'oklch(0.7 0.12 85)',
      }),
      /** Deco rose — copper tube on velvet night (not Michelin champagne twin). */
      palette(COLORSET_ID.artDecoGold, 'Art Deco Gold', {
        bgBase: 'oklch(0.09 0.02 40)',
        bgSurface: 'oklch(0.15 0.025 42)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.72 0.11 70)',
        brandAccent: 'oklch(0.78 0.1 35)',
      }),
      /** Day parlor smoke — mauve ash paper (not cayenne twin). */
      palette(COLORSET_ID.smokeVelvet, 'Smoke Velvet', {
        bgBase: 'oklch(0.96 0.015 30)',
        bgSurface: 'oklch(0.99 0.008 30)',
        textMain: 'oklch(0.22 0.03 25)',
        textMuted: 'oklch(0.46 0.025 30)',
        brandPrimary: 'oklch(0.42 0.08 25)',
        brandAccent: 'oklch(0.62 0.1 75)',
      }),
      palette(COLORSET_ID.emeraldBooth, 'Emerald Booth', {
        bgBase: 'oklch(0.1 0.03 160)',
        bgSurface: 'oklch(0.16 0.04 160)',
        textMain: 'oklch(0.9 0.02 150)',
        textMuted: 'oklch(0.72 0.03 155)',
        brandPrimary: 'oklch(0.58 0.12 160)',
        brandAccent: 'oklch(0.75 0.1 85)',
      }),
      /** Day parlor — ivory ash, burgundy jewel, sage garnish (not ketchup twin). */
      palette(COLORSET_ID.champagneAsh, 'Champagne Ash', {
        bgBase: 'oklch(0.96 0.015 80)',
        bgSurface: 'oklch(0.99 0.008 80)',
        textMain: 'oklch(0.22 0.03 40)',
        textMuted: 'oklch(0.46 0.02 55)',
        brandPrimary: 'oklch(0.42 0.07 15)',
        brandAccent: 'oklch(0.62 0.08 250)',
      }),
    ],
  }),
  /**
   * Bold Street — Döner / grill Imbiss window. Neon asphalt; not Currywurst rail,
   * not falafel herb stall. Dual-mounted on food_truck (family gate → food_truck).
   */
  [THEME_ID.boldStreet]: themeEntry(THEME_ID.boldStreet, {
    name: 'Bold Street',
    familyId: FAMILY_ID.imbiss,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.foodTruck, PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.streetNeon,
    corner_enum: CORNER_ENUM_KEYS.subtle,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.streetBold,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.streetBold],
      spacing: SPACING_ENUM_KEYS.compact,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.subtle],
    color_sets: [
      /** Poster — street neon wrap on asphalt. */
      palette(COLORSET_ID.streetNeon, 'Street Neon', {
        bgBase: 'oklch(0.08 0.005 24)',
        bgSurface: 'oklch(0.14 0.01 24)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.76 0.26 65)',
        brandAccent: 'oklch(0.84 0.22 80)',
      }),
      /** Cool night asphalt — ice-blue strip + chili spark (not Street Neon twin). */
      palette(COLORSET_ID.asphaltBold, 'Asphalt Bold', {
        bgBase: 'oklch(0.1 0.02 250)',
        bgSurface: 'oklch(0.16 0.025 248)',
        textMain: 'oklch(0.92 0.012 240)',
        textMuted: 'oklch(0.74 0.015 245)',
        brandPrimary: 'oklch(0.78 0.12 230)',
        brandAccent: 'oklch(0.72 0.16 25)',
      }),
      /** Sunset wrap — violet-magenta neon on asphalt (widens Bold Street ΔH). */
      palette(COLORSET_ID.sunsetPop, 'Sunset Pop', {
        bgBase: 'oklch(0.09 0.02 320)',
        bgSurface: 'oklch(0.15 0.025 320)',
        textMain: 'oklch(0.90 0.01 24)',
        textMuted: 'oklch(0.74 0.01 24)',
        brandPrimary: 'oklch(0.7 0.2 310)',
        brandAccent: 'oklch(0.78 0.16 45)',
      }),
      /** Ketchup day — tomato red + electric mint (not mustard-rail twin). */
      palette(COLORSET_ID.ketchupMustard, 'Ketchup Mustard', {
        bgBase: 'oklch(0.97 0.02 85)',
        bgSurface: 'oklch(0.995 0.01 85)',
        textMain: 'oklch(0.2 0.04 30)',
        textMuted: 'oklch(0.46 0.03 40)',
        brandPrimary: 'oklch(0.5 0.16 355)',
        brandAccent: 'oklch(0.68 0.12 140)',
      }),
      /** Soft pide cream — toasted sesame jewel, sumac spark (not mint / sesame twin). */
      palette(COLORSET_ID.pideCream, 'Pide Cream', {
        bgBase: 'oklch(0.97 0.016 88)',
        bgSurface: 'oklch(0.995 0.007 88)',
        textMain: 'oklch(0.22 0.035 50)',
        textMuted: 'oklch(0.48 0.025 55)',
        brandPrimary: 'oklch(0.46 0.075 48)',
        brandAccent: 'oklch(0.58 0.12 8)',
      }),
    ],
  }),
  /**
   * Mustard Rail — Currywurst / German street stand under the Hochbahn.
   * Sharp corners, condensed type; not Bold Street neon wrap, not tahini herb.
   */
  [THEME_ID.mustardRail]: themeEntry(THEME_ID.mustardRail, {
    name: 'Mustard Rail',
    familyId: FAMILY_ID.imbiss,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.mustardPaper,
    corner_enum: CORNER_ENUM_KEYS.sharp,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.mustardRail,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.mustardRail],
      spacing: SPACING_ENUM_KEYS.compact,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.sharp],
    color_sets: [
      /** Poster — mustard rail paper with ketchup stripe. */
      palette(COLORSET_ID.mustardPaper, 'Mustard Paper', {
        bgBase: 'oklch(0.96 0.03 95)',
        bgSurface: 'oklch(0.99 0.015 95)',
        textMain: 'oklch(0.22 0.04 40)',
        textMuted: 'oklch(0.45 0.03 50)',
        brandPrimary: 'oklch(0.6 0.16 95)',
        brandAccent: 'oklch(0.55 0.2 30)',
      }),
      /** Tram night — cool rail blue under the Hochbahn (widens ΔH). */
      palette(COLORSET_ID.tramAsphalt, 'Tram Asphalt', {
        bgBase: 'oklch(0.15 0.02 250)',
        bgSurface: 'oklch(0.21 0.025 250)',
        textMain: 'oklch(0.94 0.015 250)',
        textMuted: 'oklch(0.72 0.02 245)',
        brandPrimary: 'oklch(0.7 0.08 245)',
        brandAccent: 'oklch(0.72 0.12 75)',
      }),
      /** Ketchup signal — curry stand red + rail yellow (distinct from Bold Street mint). */
      palette(COLORSET_ID.ketchupSignal, 'Ketchup Signal', {
        bgBase: 'oklch(0.97 0.02 80)',
        bgSurface: 'oklch(0.995 0.01 80)',
        textMain: 'oklch(0.2 0.05 30)',
        textMuted: 'oklch(0.46 0.03 40)',
        brandPrimary: 'oklch(0.5 0.16 5)',
        brandAccent: 'oklch(0.7 0.12 95)',
      }),
      /** Pommes — fryer gold, ketchup red garnish (not beeswax twin). */
      palette(COLORSET_ID.pommesGold, 'Pommes Gold', {
        bgBase: 'oklch(0.95 0.04 85)',
        bgSurface: 'oklch(0.99 0.02 85)',
        textMain: 'oklch(0.24 0.04 55)',
        textMuted: 'oklch(0.48 0.03 60)',
        brandPrimary: 'oklch(0.58 0.13 88)',
        brandAccent: 'oklch(0.5 0.14 25)',
      }),
      palette(COLORSET_ID.grillChar, 'Grill Char', {
        bgBase: 'oklch(0.12 0.01 40)',
        bgSurface: 'oklch(0.18 0.015 40)',
        textMain: 'oklch(0.92 0.02 90)',
        textMuted: 'oklch(0.7 0.03 85)',
        brandPrimary: 'oklch(0.72 0.12 58)',
        brandAccent: 'oklch(0.65 0.1 220)',
      }),
    ],
  }),
  /**
   * Tahini Window — Falafel / mezze Imbiss. Soft type + rounded; herb greens.
   * Not Döner neon, not Currywurst rail.
   */
  [THEME_ID.tahiniWindow]: themeEntry(THEME_ID.tahiniWindow, {
    name: 'Tahini Window',
    familyId: FAMILY_ID.imbiss,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    default_colorset_id: COLORSET_ID.hummusPaper,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.tahiniWindow,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.tahiniWindow],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — hummus noon cream with parsley. */
      palette(COLORSET_ID.hummusPaper, 'Hummus Paper', {
        bgBase: 'oklch(0.97 0.02 95)',
        bgSurface: 'oklch(0.995 0.01 95)',
        textMain: 'oklch(0.28 0.04 145)',
        textMuted: 'oklch(0.48 0.03 140)',
        brandPrimary: 'oklch(0.52 0.12 145)',
        brandAccent: 'oklch(0.72 0.1 95)',
      }),
      /** Oregano night — herb soot, chili oil spark (not matcha-grid twin). */
      palette(COLORSET_ID.oreganoNight, 'Oregano Night', {
        bgBase: 'oklch(0.18 0.03 150)',
        bgSurface: 'oklch(0.24 0.035 150)',
        textMain: 'oklch(0.94 0.02 100)',
        textMuted: 'oklch(0.72 0.03 140)',
        brandPrimary: 'oklch(0.68 0.11 158)',
        brandAccent: 'oklch(0.72 0.12 35)',
      }),
      /** Lemon stall — pith yellow, olive leaf (not yuzu twin). */
      palette(COLORSET_ID.lemonStall, 'Lemon Stall', {
        bgBase: 'oklch(0.97 0.04 105)',
        bgSurface: 'oklch(0.995 0.02 105)',
        textMain: 'oklch(0.28 0.05 150)',
        textMuted: 'oklch(0.48 0.03 140)',
        brandPrimary: 'oklch(0.6 0.14 100)',
        brandAccent: 'oklch(0.55 0.12 150)',
      }),
      /** Sesame cream — toasted brown jewel, sumac pink garnish. */
      palette(COLORSET_ID.sesameCream, 'Sesame Cream', {
        bgBase: 'oklch(0.96 0.025 80)',
        bgSurface: 'oklch(0.99 0.012 80)',
        textMain: 'oklch(0.26 0.03 60)',
        textMuted: 'oklch(0.48 0.025 70)',
        brandPrimary: 'oklch(0.5 0.08 58)',
        brandAccent: 'oklch(0.58 0.12 350)',
      }),
      /** Zaatar dusk — spice green night, pomegranate garnish (not forest twin). */
      palette(COLORSET_ID.zaatarDusk, 'Zaatar Dusk', {
        bgBase: 'oklch(0.2 0.03 140)',
        bgSurface: 'oklch(0.26 0.035 140)',
        textMain: 'oklch(0.93 0.02 95)',
        textMuted: 'oklch(0.72 0.03 130)',
        brandPrimary: 'oklch(0.6 0.1 135)',
        brandAccent: 'oklch(0.7 0.12 15)',
      }),
    ],
  }),
  [THEME_ID.cleanNomad]: themeEntry(THEME_ID.cleanNomad, {
    name: 'Clean Nomad',
    familyId: FAMILY_ID.foodTruck,
    allowed_verticals: [PLATFORM_VERTICAL_KEYS.foodTruck],
    default_colorset_id: COLORSET_ID.coastalBlue,
    corner_enum: CORNER_ENUM_KEYS.rounded,
    typography_enum: TYPOGRAPHY_ENUM_KEYS.cleanSans,
    typography_tokens: {
      ...TYPOGRAPHY_PRESETS[TYPOGRAPHY_ENUM_KEYS.cleanSans],
      spacing: SPACING_ENUM_KEYS.balanced,
    },
    corner_tokens: CORNER_PRESETS[CORNER_ENUM_KEYS.rounded],
    color_sets: [
      /** Poster — coastal blue truck wrap. */
      palette(COLORSET_ID.coastalBlue, 'Coastal Blue', {
        bgBase: 'oklch(0.97 0.01 200)',
        bgSurface: 'oklch(1 0 0)',
        textMain: 'oklch(0.18 0.02 200)',
        textMuted: 'oklch(0.45 0.02 200)',
        brandPrimary: 'oklch(0.55 0.14 200)',
        brandAccent: 'oklch(0.72 0.1 175)',
      }),
      /** Sand dune — desert paper, ocean accent (not bistro linen twin). */
      palette(COLORSET_ID.sandDune, 'Sand Dune', {
        bgBase: 'oklch(0.96 0.02 85)',
        bgSurface: 'oklch(0.99 0.01 85)',
        textMain: 'oklch(0.2 0.02 55)',
        textMuted: 'oklch(0.48 0.02 55)',
        brandPrimary: 'oklch(0.5 0.08 75)',
        brandAccent: 'oklch(0.58 0.1 210)',
      }),
      /** Night trail — pine canopy, amber trail marker (not Limestone/tahini twin). */
      palette(COLORSET_ID.forestTrail, 'Forest Trail', {
        bgBase: 'oklch(0.16 0.03 145)',
        bgSurface: 'oklch(0.22 0.032 145)',
        textMain: 'oklch(0.9 0.02 140)',
        textMuted: 'oklch(0.7 0.025 145)',
        brandPrimary: 'oklch(0.68 0.1 155)',
        brandAccent: 'oklch(0.7 0.1 55)',
      }),
      palette(COLORSET_ID.canyonDust, 'Canyon Dust', {
        bgBase: 'oklch(0.94 0.04 55)',
        bgSurface: 'oklch(0.98 0.02 55)',
        textMain: 'oklch(0.22 0.04 45)',
        textMuted: 'oklch(0.48 0.03 50)',
        brandPrimary: 'oklch(0.5 0.11 58)',
        brandAccent: 'oklch(0.58 0.1 215)',
      }),
      /** Arctic night — cold fog after the trail (second dark beside Forest Trail). */
      palette(COLORSET_ID.arcticFog, 'Arctic Fog', {
        bgBase: 'oklch(0.14 0.02 235)',
        bgSurface: 'oklch(0.2 0.025 235)',
        textMain: 'oklch(0.92 0.012 240)',
        textMuted: 'oklch(0.72 0.018 235)',
        brandPrimary: 'oklch(0.72 0.06 235)',
        brandAccent: 'oklch(0.68 0.1 200)',
      }),
    ],
  }),
};
